import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@apollo/react-hooks";
import { useDispatch } from "@dhn/components/contexts/app-context";
import { useAuth0 } from "@dhn/components/contexts/react-auth0-spa";
import {
  GET_COMPANY_INFO,
  getCompanyDisplayName,
} from "@dhn/shared/company-info";
import {
  GET_PERMISSION_VIEWS_BY_PROFILE,
  GET_PHARMACY_PROVIDER_LOGO,
  GET_PRESCRIPTION_AVAILABLE_BY_DOCTOR,
} from "../queries";
import noLogo from "../../../assets/img/brand/noLogo";

export default function useNavbarDoctorOffice({ dataDoctor, profile }) {
  const dispatch = useDispatch();
  const { logout, getTokenSilently } = useAuth0();

  const { data: { getPermissionViewsByProfile } = {} } = useQuery(
    GET_PERMISSION_VIEWS_BY_PROFILE,
    {
      skip: !dataDoctor,
      variables: {
        where: { profileId: dataDoctor?.profileId },
      },
    },
  );

  const { data: { byMedicalStaffProviderpharmacyProviderLogo } = {} } = useQuery(
    GET_PHARMACY_PROVIDER_LOGO,
    {
      skip: !dataDoctor,
      // El navbar se monta en cada módulo: se muestra el valor en caché al
      // instante y se refresca en segundo plano para que no parpadee.
      fetchPolicy: "cache-and-network",
      variables: {
        where: {
          pharmacyProviderId: dataDoctor?.centerId,
          branchOfficeId: dataDoctor?.branchOfficeId,
        },
      },
    },
  );
  const { data: { getPrescriptionAvailableByDoctor } = {} } = useQuery(
    GET_PRESCRIPTION_AVAILABLE_BY_DOCTOR,
    {
      skip: !dataDoctor?.medicalStaffId,
      fetchPolicy: "cache-and-network",
      variables: {
        where: {
          medicalStaffId: dataDoctor?.medicalStaffId,
          pharmacyProviderId: dataDoctor?.centerId,
          branchOfficeId: dataDoctor?.branchOfficeId,
        },
      },
    },
  );
  const { data: { legalEntity: companyInfo } = {}, loading: loadingCompanyInfo } = useQuery(GET_COMPANY_INFO, {
    skip: !dataDoctor,
    fetchPolicy: "cache-and-network",
  });

  const permissionViews = useMemo(
    () => (Array.isArray(getPermissionViewsByProfile) ? getPermissionViewsByProfile : []),
    [getPermissionViewsByProfile],
  );

  const branchOfficeOption = useMemo(
    () =>
      profile?.doctorProfiles?.filter(
        (item) => item.centerId === dataDoctor?.centerId,
      ) || [],
    [profile?.doctorProfiles, dataDoctor?.centerId],
  );

  const [logo, setLogo] = useState(
    sessionStorage.getItem("imageLogo") || noLogo,
  );
  const [selectedBranchId, setSelectedBranchId] = useState(
    dataDoctor?.branchOfficeId?.toString() || "",
  );

  const logoProvider =
    byMedicalStaffProviderpharmacyProviderLogo?.pharmacyProviderLogo;
  const imageLogoSession = sessionStorage.getItem("imageLogo") || null;
  const latestLogoFile = logoProvider?.length
    ? logoProvider[logoProvider.length - 1]
    : null;

  const signOut = useCallback(
    (e) => {
      e?.preventDefault?.();
      sessionStorage.removeItem("doctorProfile");
      sessionStorage.removeItem("imageLogo");
      sessionStorage.removeItem("imageLogoFileName");
      dispatch({ type: "LOGOUT" });
      logout({ returnTo: window.location.origin });
    },
    [dispatch, logout],
  );

  useEffect(() => {
    const syncLogoFromSession = () => {
      setLogo(sessionStorage.getItem("imageLogo") || noLogo);
    };

    window.addEventListener("dhn:logo-updated", syncLogoFromSession);
    window.addEventListener("storage", syncLogoFromSession);

    return () => {
      window.removeEventListener("dhn:logo-updated", syncLogoFromSession);
      window.removeEventListener("storage", syncLogoFromSession);
    };
  }, []);

  useEffect(() => {
    setSelectedBranchId(dataDoctor?.branchOfficeId?.toString() || "");
  }, [dataDoctor?.branchOfficeId]);

  useEffect(() => {
    async function getLogoPharmacy(logoFile) {
      const accessToken = await getTokenSilently();
      const uri = `${process.env.REACT_APP_APOLLO_CLIENT_URI}/claim-files/${logoFile.name}`;

      const resp = await fetch(uri, {
        body: JSON.stringify(logoFile),
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${accessToken}`,
        },
      });

      if (resp.status >= 200 && resp.status <= 201) {
        const arrayBuffer = await resp.arrayBuffer();
        const base64String = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(new Blob([arrayBuffer]));
        });

        const base64Image = base64String;
        setLogo(base64Image);
        sessionStorage.setItem("imageLogo", base64Image);
        sessionStorage.setItem("imageLogoFileName", logoFile?.name || "");
        window.dispatchEvent(new Event("dhn:logo-updated"));
      }
    }

    const logoNameInSession = sessionStorage.getItem("imageLogoFileName") || "";

    if (latestLogoFile?.name && logoNameInSession !== latestLogoFile.name) {
      getLogoPharmacy(latestLogoFile);
      return;
    }

    if (imageLogoSession) {
      setLogo(imageLogoSession);
      return;
    }

    setLogo(noLogo);
  }, [latestLogoFile, getTokenSilently, imageLogoSession]);

  const changeBranch = useCallback((branchProfile) => {
    sessionStorage.setItem("doctorProfile", JSON.stringify(branchProfile));
    window.location.reload();
  }, []);

  const currentBranchProfile = useMemo(
    () =>
      branchOfficeOption.find(
        (b) => String(b.branchOfficeId) === String(selectedBranchId),
      ) || branchOfficeOption[0],
    [branchOfficeOption, selectedBranchId],
  );

  const currentBranchName =
    currentBranchProfile?.branchOfficeName ||
    dataDoctor?.branchOfficeName ||
    "Seleccionar sucursal";

  const isPermissionsReady = useMemo(
    () => Array.isArray(getPermissionViewsByProfile),
    [getPermissionViewsByProfile],
  );

  return {
    permissionViews,
    branchOfficeOption,
    currentBranchName,
    selectedBranchId,
    logo,
    companyInfo,
    isCompanyLogoLoading: loadingCompanyInfo && !companyInfo,
    companyName: getCompanyDisplayName(companyInfo),
    signOut,
    changeBranch,
    isPermissionsReady,
    prescriptionAvailableAmount: getPrescriptionAvailableByDoctor?.amount || 0,
  };
}
